import { Grid } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { Filter } from 'components/filter';
import { PedidoTipoEntregaAutoComplete } from 'pages/compra/pedido/components/atoms/filter/PedidoTipoEntregaAutoComplete';
import { useApplicationContext } from 'context/ApplicationContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useFilter } from 'hooks/useFilter';
import { useValidationUsuario } from 'hooks/useValidationUsuario';
import {
    PedidoChangeConfigModal,
    PedidoConfigPedidoAutoComplete,
    PedidoFuncionarioAutoComplete,
    PedidoGrupoAutoComplete,
    PedidoMarcaGrupoAutoComplete,
    PedidoProdutoTextField,
    PedidoPromocaoSwitch,
} from 'pages/compra/pedido/components';
import { usePedidoFilterContext } from 'pages/compra/pedido/contexts/PedidoFilterContext';
import { useSearchParamsFilters } from 'pages/compra/pedido/hooks';

export function PedidoCompraListFuncionarioFilter() {
    const { hasPermission } = usePermissionsContext();
    const hasPermissionFinalizacaoGeral = hasPermission(['COMPRA_PEDIDO_FINALIZACAO_GERAL']);
    const { setConfigPedido, setLoja, setTipoEntrega, setFuncionario } = usePedidoCompraContext();
    const { handleFilterSubmit, handleFilterReset, setDefaultFilters } = useFilter();
    const { isDownLg } = useBreakpoints();
    const { configPedidoParam, funcionarioParam, tipoEntregaParam, lojaParam } = useSearchParamsFilters();
    const { user } = useApplicationContext();
    const { validateUsuario } = useValidationUsuario();
    const {
        filterConfigPedido,
        filterDsProduto,
        filterFuncionario,
        filterCategoria,
        filterLoja,
        filterMarca,
        filterCdProduto,
        filterTipoEntrega,
        setFilterCdProduto,
        setFilterConfigPedido,
        setFilterDsProduto,
        setFilterFuncionario,
        setFilterCategoria,
        setFilterLoja,
        setFilterMarca,
        setFilterTipoEntrega,
        setDefaultFilterLoja,
        openChangeConfigModal,
        setOpenChangeConfigModal,
        validateChangeCarrinho,
        validateConfigPedido,
        filterPromocao,
        setFilterPromocao,
    } = usePedidoFilterContext();

    const hasFuncionarioTipoEntregaAmbos = (funcionario: any): boolean => funcionario && funcionario.idTipoEntrega === -1; // -1 = Selecionar ambos
    const hasUsuarioTipoEntregaAmbos = user.tipoEntrega?.idTipoEntrega === -1; // -1 = Selecionar ambos

    // Exibe o filtro de tipo de entrega caso o funcionário ou o usúário tenham a opção de entrega de ambos em seu cadastro.
    const showFilterTipoEntrega = filterFuncionario.value ? hasFuncionarioTipoEntregaAmbos(filterFuncionario.value) : hasUsuarioTipoEntregaAmbos;

    const colSpanLastRow = showFilterTipoEntrega || hasPermissionFinalizacaoGeral ? 3 : 4;

    const filters = [
        { value: filterLoja, setter: setFilterLoja },
        { value: filterConfigPedido, setter: setFilterConfigPedido },
        { value: filterDsProduto, setter: setFilterDsProduto },
        { value: filterCdProduto, setter: setFilterCdProduto },
        { value: filterCategoria, setter: setFilterCategoria },
        { value: filterTipoEntrega, setter: setFilterTipoEntrega },
        { value: filterFuncionario, setter: setFilterFuncionario },
        { value: filterMarca, setter: setFilterMarca },
        { value: filterPromocao, setter: setFilterPromocao },
    ];

    /**
     * Valida se o tipo de entrega está preenchido.
     */
    const validateTipoEntrega = () => {
        const isValid = !showFilterTipoEntrega || filterTipoEntrega.value;

        if (!isValid) enqueueSnackbar('Tipo de entrega é obrigatório', { variant: 'error' });

        return isValid;
    };

    /**
     * Valida se o funcionário está válido.
     */
    const validateFuncionario = (funcionario: any) => funcionario || validateUsuario();

    /**
     * Valida os filtros e inicia o filtro.
     */
    const startFilter = () => {
        if (!validateFuncionario(filterFuncionario.value) || !validateTipoEntrega() || !validateConfigPedido() || !validateChangeCarrinho()) return;

        handleFilterSubmit({ filters });

        setLoja(filterLoja.value);
        setConfigPedido(filterConfigPedido.value);
        setFuncionario(filterFuncionario.value);
        setTipoEntrega(filterTipoEntrega.value);
    };

    /**
     * Reseta os filtros e define a loja padrão.
     */
    const resetFilter = () => {
        handleFilterReset({ filters });

        setDefaultFilterLoja();
    };

    /**
     * Define os estados iniciais dos filtros.
     */
    const setInitialStates = () => {
        if (lojaParam && configPedidoParam) {
            setLoja(lojaParam);
            setConfigPedido(configPedidoParam);
        }

        if (funcionarioParam) {
            setFuncionario(funcionarioParam);

            if (tipoEntregaParam && hasFuncionarioTipoEntregaAmbos(funcionarioParam)) {
                // Caso tenha um parâmetro de tipo de entrega e o funcionario possua um tipo de entrega de ambos,
                // seleciona o tipo de entrega do parâmetro.

                setTipoEntrega(tipoEntregaParam);
            }
        } else if (tipoEntregaParam && hasUsuarioTipoEntregaAmbos) {
            // Caso não tenha parâmetro de funcionário, valida se o usuário logado tem o tipo de entrega de ambos
            // e seleciona o tipo de entrega do parâmetro
            setTipoEntrega(tipoEntregaParam);
        }
    };

    /**
     * Define a loja padrão e define os estados iniciais dos filtros.
     */
    const initFilter = () => {
        setDefaultFilterLoja();

        if (!validateFuncionario(funcionarioParam)) return;

        setInitialStates();

        setDefaultFilters({ filters, preventQueryParamsEmptyStart: true });
    };

    return (
        <>
            <PedidoChangeConfigModal open={openChangeConfigModal} setOpen={setOpenChangeConfigModal} filters={filters} />

            <Filter init={initFilter} reset={resetFilter} submit={startFilter} fixed={isDownLg} preventOnEnter>
                {hasPermissionFinalizacaoGeral && (
                    <Grid size={{ lg: showFilterTipoEntrega ? 4 : 6 }}>
                        <PedidoFuncionarioAutoComplete />
                    </Grid>
                )}

                {showFilterTipoEntrega && (
                    <Grid size={{ lg: hasPermissionFinalizacaoGeral ? 4 : 6 }}>
                        <PedidoTipoEntregaAutoComplete />
                    </Grid>
                )}

                <Grid size={{ lg: hasPermissionFinalizacaoGeral && showFilterTipoEntrega ? 4 : 6 }}>
                    <PedidoConfigPedidoAutoComplete />
                </Grid>

                <Grid size={{ lg: hasPermissionFinalizacaoGeral || showFilterTipoEntrega ? 3 : 6 }}>
                    <PedidoMarcaGrupoAutoComplete />
                </Grid>

                <Grid size={{ lg: colSpanLastRow }}>
                    <PedidoGrupoAutoComplete />
                </Grid>

                <Grid size={{ lg: colSpanLastRow }}>
                    <PedidoProdutoTextField />
                </Grid>

                <Grid size={{ lg: colSpanLastRow }}>
                    <PedidoPromocaoSwitch />
                </Grid>
            </Filter>
        </>
    );
}
